// CareWorldHome.jsx
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Stakeholders
const stakeholders = ["Family / Customer", "Caretaker", "Agency", "Admin"];

// Activities / Use Cases
const activities = [
  { id: "babyCare", label: "Baby Care" },
  { id: "elderCare", label: "Elder Care" },
  { id: "homeTasks", label: "Home Tasks" },
  { id: "medicalCare", label: "Medical Care" },
];

// Fallback dummy data in Indian context
const fallbackData = {
  babyCare: [
    {
      name: "Ananya Sharma",
      experience: 3,
      rating: 4.9,
      location: "Mumbai",
      hourlyRate: 150,
      dailyRate: 1000,
      monthlyRate: 25000,
      tasks: ["Feeding", "Diaper Change", "Nap Monitoring"],
      diary: ["Baby slept 2 hrs", "Milk intake: 120ml", "Played with toys"],
      availability: { hourly: ["9 AM - 12 PM", "2 PM - 5 PM", "6 PM - 8 PM"] },
    },
    {
      name: "Rohan Verma",
      experience: 2,
      rating: 4.7,
      location: "Delhi",
      hourlyRate: 130,
      dailyRate: 900,
      monthlyRate: 22000,
      tasks: ["Feeding", "Storytelling", "Nap Monitoring"],
      diary: ["Baby played with blocks", "Milk intake: 100ml", "Nap 1.5 hrs"],
      availability: { hourly: ["10 AM - 1 PM", "3 PM - 6 PM"] },
    },
  ],
  elderCare: [
    {
      name: "Seema Gupta",
      experience: 5,
      rating: 4.8,
      location: "Bangalore",
      hourlyRate: 200,
      dailyRate: 1200,
      monthlyRate: 30000,
      tasks: ["Medication Reminder", "Mobility Assistance", "Meal Preparation"],
      diary: ["Walked 500m", "Medicine given at 8 AM", "Lunch prepared: Dal & Roti"],
      availability: { hourly: ["8 AM - 12 PM", "2 PM - 6 PM"] },
    },
    {
      name: "Prakash Iyer",
      experience: 4,
      rating: 4.6,
      location: "Chennai",
      hourlyRate: 180,
      dailyRate: 1000,
      monthlyRate: 28000,
      tasks: ["Medication Reminder", "Exercise Monitoring", "Companionship"],
      diary: ["Morning walk 20 mins", "Medicine given at 9 AM", "Played cards"],
      availability: { hourly: ["9 AM - 1 PM", "3 PM - 7 PM"] },
    },
  ],
  homeTasks: [
    {
      name: "Lakshmi Nair",
      experience: 4,
      rating: 4.7,
      location: "Hyderabad",
      hourlyRate: 120,
      dailyRate: 800,
      monthlyRate: 20000,
      tasks: ["Cleaning", "Cooking", "Laundry"],
      diary: ["Deep cleaned living room", "Prepared South Indian meal", "Ironed clothes"],
      availability: { hourly: ["7 AM - 11 AM", "1 PM - 5 PM"] },
    },
    {
      name: "Rajesh Kumar",
      experience: 3,
      rating: 4.5,
      location: "Kolkata",
      hourlyRate: 110,
      dailyRate: 750,
      monthlyRate: 18000,
      tasks: ["Grocery Shopping", "Dish Washing", "Gardening"],
      diary: ["Bought groceries from market", "Washed all utensils", "Watered plants"],
      availability: { hourly: ["9 AM - 1 PM", "4 PM - 7 PM"] },
    },
  ],
  medicalCare: [
    {
      name: "Nurse Meera Joshi",
      experience: 7,
      rating: 4.9,
      location: "Ahmedabad",
      hourlyRate: 250,
      dailyRate: 1500,
      monthlyRate: 35000,
      tasks: ["Vital Checks", "Medication Admin", "Wound Care"],
      diary: ["BP: 120/80", "Administered insulin", "Dressed wound"],
      availability: { hourly: ["8 AM - 12 PM", "2 PM - 6 PM"] },
    },
    {
      name: "Physio Ravi",
      experience: 5,
      rating: 4.6,
      location: "Jaipur",
      hourlyRate: 220,
      dailyRate: 1300,
      monthlyRate: 32000,
      tasks: ["Physiotherapy", "Exercise Guidance", "Pain Management"],
      diary: ["Leg exercises 30 mins", "Guided yoga poses", "Applied hot pack"],
      availability: { hourly: ["10 AM - 2 PM", "5 PM - 8 PM"] },
    },
  ],
};

// Experience filter options
const experienceOptions = ["0-2", "3-5", "6+"];

const CareWorldHome = () => {
  const [selectedStakeholder, setSelectedStakeholder] = useState(stakeholders[0]);
  const [activeTab, setActiveTab] = useState(activities[0].id);
  const [nestedTab, setNestedTab] = useState("hourly");
  const [selectedCaretaker, setSelectedCaretaker] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([null, null]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterRating, setFilterRating] = useState("");
  const [filterExperience, setFilterExperience] = useState("");

  const openBookingModal = (caretaker) => {
    setSelectedCaretaker(caretaker);
    setIsBookingOpen(true);
    setSelectedTime("");
    setSelectedDate(null);
    setSelectedDates([null, null]);
  };

  const closeBookingModal = () => {
    setIsBookingOpen(false);
    setSelectedCaretaker(null);
  };

  const handleBook = () => {
    let bookedInfo = "";
    if (nestedTab === "hourly") bookedInfo = selectedTime;
    if (nestedTab === "daily") bookedInfo = selectedDate?.toLocaleDateString();
    if (nestedTab === "monthly" && selectedDates[0] && selectedDates[1])
      bookedInfo = `${selectedDates[0].toLocaleDateString()} - ${selectedDates[1].toLocaleDateString()}`;
    alert(`Booked ${selectedCaretaker.name} for ${bookedInfo} (${nestedTab})`);
    closeBookingModal();
  };

  // Fallback to empty array if no data for the active tab
  const currentData = fallbackData[activeTab] || [];
  
  // Filtered caretakers
  const filteredCaretakers = currentData.filter((c) => {
    let matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    let matchesCity = filterCity ? c.location === filterCity : true;
    let matchesRating = filterRating ? c.rating >= parseFloat(filterRating) : true;
    let matchesExp = true;
    if (filterExperience) {
      if (filterExperience === "0-2") matchesExp = c.experience <= 2;
      if (filterExperience === "3-5") matchesExp = c.experience >= 3 && c.experience <= 5;
      if (filterExperience === "6+") matchesExp = c.experience >= 6;
    }
    return matchesSearch && matchesCity && matchesRating && matchesExp;
  });

  // Unique cities for filter dropdown
  const uniqueCities = [...new Set(currentData.map((c) => c.location))];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h1 className="text-2xl font-bold mb-2 sm:mb-0">CareWorld Home</h1>
        <select
          className="bg-gray-800 text-gray-100 p-2 h-10 rounded focus:outline-none"
          value={selectedStakeholder}
          onChange={(e) => setSelectedStakeholder(e.target.value)}
        >
          {stakeholders.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Activity Tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 mb-1 place-items-center">
        {activities.map((act) => (
          <button
            key={act.id}
            className={`px-4 py-2 rounded-m whitespace-nowrap ${
              activeTab === act.id
                ? "bg-blue-600 text-white"
                : "bg-gray-800 text-gray-300"
            }`}
            onClick={() => setActiveTab(act.id)}
          >
            {act.label}
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col justify-center border border-green-500 p-2 rounded-lg">
        <div className="flex flex-row justify-left mb-2 w-full">
            <input
                type="text"
                placeholder="Search by name"
                className="p-2 rounded bg-gray-700 text-gray-100"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-6 place-items-center">
            <select
            className="p-2 rounded bg-gray-700 text-gray-100 w-full h-10"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            >
            <option value="">All Cities</option>
            {uniqueCities.map((city) => (
                <option key={city} value={city}>
                {city}
                </option>
            ))}
            </select>
            <select
            className="p-2 rounded bg-gray-700 text-gray-100 w-full h-10"
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            >
            <option value="">Rating ≥</option>
            <option value="4.5">4.5</option>
            <option value="4.0">4.0</option>
            <option value="3.5">3.5</option>
            </select>
            <select
            className="p-2 rounded bg-gray-700 text-gray-100 w-full h-10"
            value={filterExperience}
            onChange={(e) => setFilterExperience(e.target.value)}
            >
            <option value="">Experience</option>
            {experienceOptions.map((exp) => (
                <option key={exp} value={exp}>
                {exp} yrs
                </option>
            ))}
            </select>
        </div>
      </div>

      {/* Caretaker Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-4">
        {filteredCaretakers.length === 0 && (
          <p className="text-gray-400 col-span-full text-center">No caretakers found.</p>
        )}
        {filteredCaretakers.map((person, idx) => (
          <div
            key={idx}
            className="bg-gray-800 p-4 rounded-lg shadow-lg border border-green-300"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-bold">{person.name}</h2>
              <span className="text-yellow-400 font-semibold">{person.rating} ⭐</span>
            </div>
            <p className="text-gray-300 mb-1">{person.experience} yrs experience</p>
            <p className="text-gray-300 mb-1">{person.location}</p>

            {/* Nested Tabs */}
            <div className="flex space-x-2 mt-2 mb-2">
              {["hourly", "daily", "monthly"].map((type) => (
                <span
                  key={type + idx}
                  className={`px-3 py-1 text-sm rounded-full cursor-pointer ${
                    nestedTab === type
                      ? "bg-blue-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                  onClick={() => setNestedTab(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              ))}
            </div>

            {/* Availability / Rate */}
            <div className="mb-2">
              <span className="bg-green-600 text-gray-100 px-2 py-1 rounded text-sm">
                {nestedTab === "hourly"
                  ? `₹${person.hourlyRate}/hr`
                  : nestedTab === "daily"
                  ? `₹${person.dailyRate}/day`
                  : `₹${person.monthlyRate}/month`}
              </span>
            </div>

            {/* Tooltip Preview */}
            <div className="mb-2">
              <h4 className="font-semibold text-sm">Tasks Preview:</h4>
              <p className="text-gray-400 text-sm truncate" title={person.tasks.join(", ")}>
                {person.tasks.join(", ")}
              </p>
            </div>
            <div className="mb-2">
              <h4 className="font-semibold text-sm">Diary Preview:</h4>
              <p className="text-gray-400 text-sm truncate" title={person.diary.join(", ")}>
                {person.diary.join(", ")}
              </p>
            </div>

            <button
              className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
              onClick={() => openBookingModal(person)}
            >
              Book Now
            </button>
          </div>
        ))}
      </div>

      {/* Booking Modal */}
      {isBookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md mx-auto">
            <h2 className="text-xl font-bold mb-4">
              Book {selectedCaretaker?.name} ({nestedTab})
            </h2>

            {/* Hourly */}
            {nestedTab === "hourly" && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-1">Select Time Slot</label>
                <select
                  className="w-full p-2 rounded bg-gray-700 text-gray-100"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                >
                  <option value="">--Select--</option>
                  {selectedCaretaker?.availability.hourly.map((slot, i) => (
                    <option key={i} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Daily */}
            {nestedTab === "daily" && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-1">Select Date</label>
                <DatePicker
                  className="w-full p-2 rounded bg-gray-700 text-gray-100"
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  minDate={new Date()}
                  placeholderText="Select a date"
                />
              </div>
            )}

            {/* Monthly */}
            {nestedTab === "monthly" && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-1">Select Date Range</label>
                <DatePicker
                  className="w-full p-2 rounded bg-gray-700 text-gray-100"
                  selected={selectedDates[0]}
                  onChange={(dates) => setSelectedDates(dates)}
                  startDate={selectedDates[0]}
                  endDate={selectedDates[1]}
                  selectsRange
                  minDate={new Date()}
                  placeholderText="Select date range"
                />
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                className="bg-gray-600 px-3 py-1 rounded hover:bg-gray-500"
                onClick={closeBookingModal}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 px-3 py-1 rounded hover:bg-blue-700"
                onClick={handleBook}
                disabled={
                  (nestedTab === "hourly" && !selectedTime) ||
                  (nestedTab === "daily" && !selectedDate) ||
                  (nestedTab === "monthly" && (!selectedDates[0] || !selectedDates[1]))
                }
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareWorldHome;